/**
 * Performance monitoring utilities for flag caching
 */

interface FlagPerformanceMetrics {
  cacheHits: number;
  cacheMisses: number;
  totalRequests: number;
  averageLoadTime: number;
  loadTimes: number[];
}

class FlagPerformanceMonitor {
  private metrics: FlagPerformanceMetrics = {
    cacheHits: 0,
    cacheMisses: 0,
    totalRequests: 0,
    averageLoadTime: 0,
    loadTimes: []
  };

  private startTime: number | null = null;

  /**
   * Record the start of a flag loading operation
   */
  startLoad(): void {
    this.startTime = performance.now();
    this.metrics.totalRequests++;
  }

  /**
   * Record a cache hit (flag was already cached)
   */
  recordCacheHit(): void {
    this.metrics.cacheHits++;
    if (this.startTime) {
      const loadTime = performance.now() - this.startTime;
      this.recordLoadTime(loadTime);
      this.startTime = null;
    }
  }

  /**
   * Record a cache miss (flag had to be loaded from network)
   */
  recordCacheMiss(): void {
    this.metrics.cacheMisses++;
    if (this.startTime) {
      const loadTime = performance.now() - this.startTime;
      this.recordLoadTime(loadTime);
      this.startTime = null;
    }
  }

  /**
   * Record the time it took to load a flag
   */
  private recordLoadTime(time: number): void {
    this.metrics.loadTimes.push(time);
    
    // Keep only the last 100 load times to prevent memory bloat
    if (this.metrics.loadTimes.length > 100) {
      this.metrics.loadTimes = this.metrics.loadTimes.slice(-100);
    }
    
    // Recalculate average
    this.metrics.averageLoadTime = 
      this.metrics.loadTimes.reduce((sum, time) => sum + time, 0) / 
      this.metrics.loadTimes.length;
  }

  /**
   * Get current performance metrics
   */
  getMetrics(): FlagPerformanceMetrics & { cacheHitRatio: number } {
    const cacheHitRatio = this.metrics.totalRequests > 0 
      ? this.metrics.cacheHits / this.metrics.totalRequests 
      : 0;

    return {
      ...this.metrics,
      cacheHitRatio
    };
  }

  /**
   * Log performance metrics to console (for development)
   */
  logMetrics(): void {
    const metrics = this.getMetrics();
    console.group('🏁 Flag Cache Performance Metrics');
    console.log(`Total Requests: ${metrics.totalRequests}`);
    console.log(`Cache Hits: ${metrics.cacheHits}`);
    console.log(`Cache Misses: ${metrics.cacheMisses}`);
    console.log(`Cache Hit Ratio: ${(metrics.cacheHitRatio * 100).toFixed(1)}%`);
    console.log(`Average Load Time: ${metrics.averageLoadTime.toFixed(2)}ms`);
    
    if (metrics.cacheHitRatio > 0.8) {
      console.log('✅ Excellent cache performance!');
    } else if (metrics.cacheHitRatio > 0.6) {
      console.log('⚠️ Good cache performance, could be better');
    } else {
      console.log('❌ Poor cache performance, consider preloading more flags');
    }
    
    console.groupEnd();
  }

  /**
   * Reset all metrics
   */
  reset(): void {
    this.metrics = {
      cacheHits: 0,
      cacheMisses: 0,
      totalRequests: 0,
      averageLoadTime: 0,
      loadTimes: []
    };
    this.startTime = null;
  }
}

// Create a singleton instance
export const flagPerformanceMonitor = new FlagPerformanceMonitor();

// Auto-log metrics every 30 seconds in development
if (process.env.NODE_ENV === 'development') {
  setInterval(() => {
    const metrics = flagPerformanceMonitor.getMetrics();
    if (metrics.totalRequests > 0) {
      flagPerformanceMonitor.logMetrics();
    }
  }, 30000);
}

export default FlagPerformanceMonitor;
